// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaContactFields from '../../../fields/RegattaContactFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaContact'>>;
  loading: boolean;
};

class RegattaContactsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaContact') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaContact({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaContact'> = {
      Email: null,
      Image: null,
      Name: '',
      Phone: null,
      RegattaId,
      Role: '',
    };

    return (
      <SectionHeader
        bottomBorder
        description={t('A list of regatta officials and management persons')}
        iconClass={'icon-people'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaContactFields item={newItem} />
            )}
            id={'create-regatta-contact-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaContact'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaContact(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta contact')}
          >
            <Button
              color={'info'}
              id={'create-regatta-contact-button'}
              size={'sm'}
              title={t('Add new regatta contact')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta contacts')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaContact'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaContactFields item={dataItem} />
          )}
          id={`update-regatta-contact-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaContact'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaContact(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta contact')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-contact-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-contact-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaContact(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta contact')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-contact-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th>{t('Name')}</th>
            <th>{t('Role')}</th>
            <th>{`${t('Email')} / ${t('Phone')}`}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaContact'>) => (
            <tr key={dataItem.id}>
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>{dataItem.Name}</td>
              <td>{dataItem.Role}</td>
              <td>{`${dataItem.Email || ''} / ${dataItem.Phone || ''}`}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaContactsSectionView;
